import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { certificateService } from "../services/api/certificate.service";
import { applicantService } from "../services/api/applicant.service";
import { officiatorService } from "../services/api/officiator.service";
import { paymentService } from "../services/api/payment.service";
import type { CertificateApplication } from "../types/certificate.types";
import type { Applicant } from "../types/applicant.types";
import { ApplicantType } from "../types/applicant.types";
import type { Officiator } from "../types/officiator.types";
import { UserStatusId } from "../types/user.types";
import { ManualPayment } from "./ManualPayment";
import { getFullPath } from "../utils/getFullPath";
import { getAccessToken } from "../api/apiClient";
import { RECAPTCHA_SITE_KEY } from "../config/api";
import {
  showSuccessToast,
  showErrorToast,
  showConfirm,
} from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import "../styles/pages/UserDetail.css";
import "../styles/pages/Users.css";

declare global {
  interface Window {
    grecaptcha?: {
      render: (el: HTMLElement, options: Record<string, unknown>) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => string;
    };
  }
}

const RECAPTCHA_SCRIPT_ID = "google-recaptcha-script";

const ensureRecaptchaScript = () => {
  if (document.getElementById(RECAPTCHA_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = RECAPTCHA_SCRIPT_ID;
  script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
};

const whenRecaptchaReady = (): Promise<void> =>
  new Promise((resolve, reject) => {
    ensureRecaptchaScript();
    const start = Date.now();
    const timer = window.setInterval(() => {
      if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
        window.clearInterval(timer);
        resolve();
      } else if (Date.now() - start > 15000) {
        window.clearInterval(timer);
        reject(new Error("reCAPTCHA failed to load (check network / domain)."));
      }
    }, 100);
  });

const PortalSummary = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const appId = Number(id);

  const [application, setApplication] = useState<CertificateApplication | null>(
    null,
  );
  const [first, setFirst] = useState<Applicant | null>(null);
  const [second, setSecond] = useState<Applicant | null>(null);
  const [officiator, setOfficiator] = useState<Officiator | null>(null);
  const [hasPayment, setHasPayment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  // CAPTCHA (must be solved before the application can be submitted)
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);

  const isDivorce = (application?.category || "").toLowerCase().includes("divorce");
  // The application can only be submitted while it's still in a pre-submission state.
  const alreadySubmitted =
    !!application &&
    application.statusId !== UserStatusId.Created &&
    application.statusId !== UserStatusId.Queried;
  // Show the payment/submit controls (incl. CAPTCHA) only before submission.
  const showSubmitControls = !!application && !loading && !alreadySubmitted;

  // Render the reCAPTCHA widget once the submit controls are on screen.
  useEffect(() => {
    if (!showSubmitControls) return;
    let cancelled = false;
    whenRecaptchaReady()
      .then(() => {
        if (
          cancelled ||
          !captchaRef.current ||
          !window.grecaptcha ||
          widgetIdRef.current !== null
        ) {
          return;
        }
        captchaRef.current.innerHTML = "";
        try {
          widgetIdRef.current = window.grecaptcha.render(captchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            callback: (token: string) => setCaptchaToken(token),
            "expired-callback": () => setCaptchaToken(""),
            "error-callback": () => setCaptchaToken(""),
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("reCAPTCHA render failed", err);
        }
      })
      // eslint-disable-next-line no-console
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, [showSubmitControls]);

  const resetCaptcha = () => {
    if (window.grecaptcha && widgetIdRef.current !== null) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
    setCaptchaToken("");
  };

  useEffect(() => {
    if (!getAccessToken()) {
      navigate("/");
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    if (!appId) return;
    try {
      setLoading(true);
      const app = await certificateService.getCertificateApplicationById(appId);
      setApplication(app);

      const divorce = (app.category || "").toLowerCase().includes("divorce");
      if (divorce) {
        const applicants =
          await applicantService.getDivorceApplicantsByCertificateId(appId);
        // Divorce applicants are returned plaintiff-first, then defendant.
        setFirst(applicants[0] || null);
        setSecond(applicants[1] || null);
      } else {
        const applicants =
          await applicantService.getApplicantsByCertificateId(appId);
        setFirst(
          applicants.find((a) => a.applicantType === ApplicantType.Groom) || null,
        );
        setSecond(
          applicants.find((a) => a.applicantType === ApplicantType.Bride) || null,
        );
        try {
          const off = await officiatorService.getOfficiatorsByCertificateId(appId);
          setOfficiator(Array.isArray(off) && off.length > 0 ? off[0] : null);
        } catch {
          setOfficiator(null);
        }
      }

      try {
        const payments = await paymentService.getPaymentsByApplicationId(appId);
        setHasPayment(Array.isArray(payments) && payments.length > 0);
      } catch {
        setHasPayment(false);
      }
    } catch {
      showErrorToast("Could not load the application.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d?: string) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return d;
    }
  };

  const handleSubmitApplication = async () => {
    if (!hasPayment || !application?.id || !captchaToken) return;
    const ok = await showConfirm(
      "Submit Application",
      "Submit this application for approval? You won't be able to edit it after submission.",
      "Yes, submit",
      "Cancel",
    );
    if (!ok) return;
    try {
      setSubmitting(true);
      await certificateService.changeStatus(application.id, UserStatusId.Submitted);
      showSuccessToast("Application submitted for approval.");
      navigate("/portal");
    } catch (err) {
      resetCaptcha();
      const message =
        err instanceof Error && err.message ? err.message : GENERIC_ERROR_MESSAGE;
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderPhoto = (applicant: Applicant | null, label: string) => {
    const url = getFullPath(applicant?.photoUrl || applicant?.profileImagePath);
    return (
      <div className="applicant-photo-col">
        <div className="user-detail-field-label">Passport Size Photo</div>
        {url ? (
          <img
            src={url}
            alt={label}
            style={{
              width: 120,
              height: 150,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              marginTop: "0.25rem",
            }}
          />
        ) : (
          <div className="user-detail-field-value empty">—</div>
        )}
      </div>
    );
  };

  const renderApplicant = (
    applicant: Applicant | null,
    title: string,
    photoLabel: string,
  ) => {
    if (!applicant) return null;
    return (
      <>
        <h3 className="user-detail-section-title">{title}</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "1.5rem",
            alignItems: "start",
          }}
        >
          {renderPhoto(applicant, photoLabel)}
          <div className="user-detail-grid">
            <div className="user-detail-field">
              <div className="user-detail-field-label">Full Name</div>
              <div className="user-detail-field-value">
                {applicant.firstName} {applicant.middleName || ""}{" "}
                {applicant.lastName}
              </div>
            </div>
            <div className="user-detail-field">
              <div className="user-detail-field-label">Date of Birth</div>
              <div className="user-detail-field-value">
                {formatDate(applicant.dateOfBirth)}
              </div>
            </div>
            <div className="user-detail-field">
              <div className="user-detail-field-label">Primary Phone</div>
              <div className="user-detail-field-value">
                {applicant.primaryPhone || "—"}
              </div>
            </div>
            <div className="user-detail-field">
              <div className="user-detail-field-label">Email</div>
              <div className="user-detail-field-value">
                {applicant.email || "—"}
              </div>
            </div>
            <div className="user-detail-field">
              <div className="user-detail-field-label">National ID</div>
              <div className="user-detail-field-value">
                {applicant.nationalIdNumber || "—"}
              </div>
            </div>
            <div className="user-detail-field">
              <div className="user-detail-field-label">Current Address</div>
              <div className="user-detail-field-value">
                {applicant.currentAddress || "—"}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="portal-detail-body">
      <header className="portal-detail-topbar">
        <button
          type="button"
          className="portal-btn portal-btn--ghost"
          onClick={() =>
            navigate(
              application && !alreadySubmitted
                ? `${isDivorce ? "/portal/divorce/apply" : "/portal/marriages/apply"}/${application.id}`
                : "/portal",
            )
          }
        >
          {alreadySubmitted ? "← My Applications" : "← Edit Application"}
        </button>
        <span className="portal-detail-title">Application Summary</span>
        <span />
      </header>

      <main className="main-content">
        <div className="user-detail-page" style={{ maxWidth: 960, margin: "0 auto" }}>
          {loading ? (
            <p className="portal-muted">Loading…</p>
          ) : !application ? (
            <p className="portal-muted">Application not found.</p>
          ) : (
            <div className="user-detail-info">
              <div className="user-detail-profile-header">
                <div className="user-detail-avatar">
                  {isDivorce ? "📄" : "💍"}
                </div>
                <div className="user-detail-profile-info">
                  <h2>{application.formNo}</h2>
                  <div className="user-detail-profile-meta">
                    <span>{application.category}</span>
                    <span className="divider">•</span>
                    <span className="status-badge status-badge--active">
                      {application.status?.name ||
                        application.statusName ||
                        "Created"}
                    </span>
                  </div>
                </div>
              </div>

              <h3 className="user-detail-section-title">
                Application Information
              </h3>
              <div
                className="user-detail-grid"
                style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
              >
                <div className="user-detail-field">
                  <div className="user-detail-field-label">Category</div>
                  <div className="user-detail-field-value">
                    {application.category}
                  </div>
                </div>
                <div className="user-detail-field">
                  <div className="user-detail-field-label">County</div>
                  <div className="user-detail-field-value">
                    {application.countyName || "—"}
                  </div>
                </div>
                <div className="user-detail-field">
                  <div className="user-detail-field-label">Place of Marriage</div>
                  <div className="user-detail-field-value">
                    {application.placeOfWedding || "—"}
                  </div>
                </div>
                <div className="user-detail-field">
                  <div className="user-detail-field-label">Date of Marriage</div>
                  <div className="user-detail-field-value">
                    {formatDate(application.dateOfWedding)}
                  </div>
                </div>
                <div className="user-detail-field">
                  <div className="user-detail-field-label">Email</div>
                  <div className="user-detail-field-value">
                    {application.email || "—"}
                  </div>
                </div>
              </div>

              {renderApplicant(
                first,
                isDivorce ? "Plaintiff's Details" : "Groom's Details",
                isDivorce ? "Plaintiff Photo" : "Groom Photo",
              )}
              {renderApplicant(
                second,
                isDivorce ? "Defendant's Details" : "Bride's Details",
                isDivorce ? "Defendant Photo" : "Bride Photo",
              )}

              {officiator && (
                <>
                  <h3 className="user-detail-section-title">
                    Officiator's Details
                  </h3>
                  <div className="user-detail-grid">
                    <div className="user-detail-field">
                      <div className="user-detail-field-label">
                        Officiator Name
                      </div>
                      <div className="user-detail-field-value">
                        {officiator.officiatorName || "—"}
                      </div>
                    </div>
                    <div className="user-detail-field">
                      <div className="user-detail-field-label">
                        Officiator Contact
                      </div>
                      <div className="user-detail-field-value">
                        {officiator.officiatorContact || "—"}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="portal-summary-actions">
                <div className="portal-payment-status">
                  Payment:{" "}
                  {hasPayment ? (
                    <span className="portal-status">Paid</span>
                  ) : (
                    <span
                      className="portal-status"
                      style={{ background: "#fef3c7", color: "#92400e" }}
                    >
                      Not paid
                    </span>
                  )}
                </div>
                {alreadySubmitted ? (
                  <span
                    className="portal-status"
                    style={{ background: "#d4edda", color: "#155724" }}
                  >
                    ✅ Submitted for approval
                  </span>
                ) : (
                  <div
                    className="portal-summary-buttons"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      type="button"
                      className="portal-btn portal-btn--primary"
                      onClick={() => setShowPayment(true)}
                      disabled={hasPayment}
                      title={
                        hasPayment
                          ? "Payment already made"
                          : "Make payment for this application"
                      }
                      style={
                        hasPayment
                          ? { opacity: 0.5, cursor: "not-allowed" }
                          : undefined
                      }
                    >
                      💳 Make Payment
                    </button>
                    <div className="captcha-field" ref={captchaRef} />
                    <button
                      type="button"
                      className="portal-btn portal-btn--primary"
                      onClick={handleSubmitApplication}
                      disabled={!hasPayment || submitting || !captchaToken}
                      title={
                        !hasPayment
                          ? "Payment is required before submitting"
                          : !captchaToken
                            ? "Complete the CAPTCHA to enable submit"
                            : "Submit for approval"
                      }
                      style={
                        !hasPayment || !captchaToken
                          ? { opacity: 0.5, cursor: "not-allowed" }
                          : undefined
                      }
                    >
                      {submitting ? "Submitting…" : "Submit Application"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {showPayment && application && (
        <ManualPayment
          application={application}
          onClose={() => setShowPayment(false)}
          onPaymentSuccess={() => {
            setShowPayment(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default PortalSummary;
